import { motion } from "framer-motion";
import { Polaroid } from "../Polaroid";
import flower1 from "../../../assets/flower1.svg";
import flower2 from "../../../assets/flower2.svg";
import border from "../../../assets/border_beige.svg";
import photo1 from "../../../assets/photo1.png";
import photo2 from "../../../assets/photo2.png";
import photo3 from "../../../assets/photo3.png";
import photo4 from "../../../assets/photo4.png";
import photo5 from "../../../assets/photo5.png";
import { sectionSpacingClass } from "../Layout";
import { useI18n } from "../i18n";

const STORY_PHOTOS = [photo1, photo2, photo3, photo4, photo5];

export default function Story() {
  const { t, get } = useI18n();
  const moments = get("story.items") || [];

  return (
    <div
      id="story"
      className={`relative overflow-hidden px-4 mb-4 bg-theme-primary ${sectionSpacingClass}`}
    >
      <img
        src={flower1}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 w-[70%] max-w-[60vw] translate-x-6 -translate-y-4 opacity-70 lg:w-1/3 lg:translate-x-10 lg:-translate-y-6"
      />

      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center sm:mb-20"
        >
          <h2 className="font-serif text-5xl md:text-6xl text-theme-secondary-text mb-4">
            {t("story.title")}
          </h2>
        </motion.div>

        <div className="space-y-24 sm:space-y-28">
          {moments.map((moment, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className={`flex flex-col ${
                index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
              } relative items-center gap-12 md:gap-20`}
            >
              {index === 1 && (
                <img
                  src={flower2}
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute left-0 top-1/2 hidden w-[70%] max-w-[60vw] -translate-y-1/2 -translate-x-1/3 opacity-70 md:block md:w-1/3"
                />
              )}

              {index === 2 && (
                <img
                  src={flower1}
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute bottom-0 right-0 hidden w-[70%] max-w-[60vw] translate-x-1/3 translate-y-1/2 scale-y-[-1] opacity-70 md:block md:w-1/3"
                />
              )}

              {/* Polaroid */}
              <div className="flex-shrink-0">
                <Polaroid
                  imageUrl={STORY_PHOTOS[index] || moment.imageUrl || "https://via.placeholder.com/400"}
                  rotation={index % 2 === 0 ? -3 : 3}
                />
              </div>

              {/* Description */}
              <div
                className={`flex-1 ${index % 2 === 0 ? "md:text-left" : "md:text-right"} text-center`}
              >
                <motion.p
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="text-theme-accent text-sm uppercase tracking-widest mb-3"
                >
                  {moment.tag}
                </motion.p>
                <motion.h3
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="font-script text-3xl md:text-4xl text-theme-secondary-text mb-4"
                >
                  {moment.title}
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  className="text-theme-secondary-text text-lg leading-relaxed max-w-md mx-auto"
                >
                  {moment.text}
                </motion.p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="py-8 sm:py-10">
          <img
            src={border}
            alt=""
            aria-hidden="true"
            className="mx-auto w-full max-w-[333px] opacity-90"
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-3xl rounded-[32px] border border-theme-border-light bg-theme-surface/90 px-8 py-4 text-center shadow-sm backdrop-blur-sm"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-theme-secondary-text">
            {t("story.note_title")}
          </p>
          <p className="mt-4 text-lg leading-relaxed text-theme-secondary-text">
            {t("story.note_text")}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
